/**
 * Gap-list export: non-complete checklist rows (Missing and Partial). N/A stays off
 * the list. xlsx uses the same zero-dependency workbook builder as the underwriting pack.
 */
import { xlsxParts } from '../engine/underwritingXlsx.js';
import { downloadBytes } from '../engine/ooxmlZip.js';
import { CATEGORY_LABEL as CATEGORY, CHECKLIST_NAME, PHASE_LABEL as PHASE, readinessHeadline } from './checklist.js';

const { buildWorkbook, headerRow, S, XLSX_MIME } = xlsxParts;

const HEADERS = ['Phase', 'Category', 'Owner', 'Due', 'ID', 'Item', 'What satisfies it', 'Method', 'Required', 'Status', 'Evidence', 'Captured', 'Note', 'Updated by', 'Updated at'];

function capturedLabel(item) {
  const values = item.override?.values || {};
  return (item.fields || [])
    .filter((f) => values[f.key] != null && values[f.key] !== '')
    .map((f) => `${f.label}: ${values[f.key]}`)
    .join('; ');
}

function rowOf(item) {
  return [
    PHASE[item.phase] || item.phase,
    CATEGORY[item.category] || item.category,
    item.owner || '',
    item.dueDate ? `${item.dueDate}${item.overdue ? ' (overdue)' : ''}` : '',
    item.id,
    item.item,
    item.whatSatisfies || '',
    item.method,
    item.requiredForPhase ? 'Y' : '',
    item.status,
    evidenceLabel(item),
    capturedLabel(item),
    item.override?.note || '',
    item.override?.userName || '',
    item.override?.updatedAt || '',
  ];
}

export function gapRows(result) {
  return (result?.gaps || result?.items || []).filter((i) => i.status === 'Missing' || i.status === 'Partial');
}

function evidenceLabel(item) {
  return (item.evidence || []).map((e) => e.label).filter(Boolean).join('; ');
}

export function gapListCsv(result) {
  const lines = [HEADERS.map(csvCell).join(',')];
  for (const item of gapRows(result)) {
    lines.push(rowOf(item).map(csvCell).join(','));
  }
  return `${lines.join('\n')}\n`;
}

function csvCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildGapListXlsx(result, { dealLabel = 'Deal' } = {}) {
  const ready = result?.readiness?.pre_offer;
  const cover = [
    { values: [`D2D Energy · ${dealLabel}`], styles: [S.brandTitle] },
    { values: [`Diligence gap list · ${CHECKLIST_NAME}`], styles: [S.brandSub] },
    [],
    { values: ['Catalog', result?.catalogVersion || ''], styles: [S.label] },
    { values: ['Stage', result?.stage || ''], styles: [S.label] },
    {
      values: [
        'Pre-offer readiness',
        ready?.pct == null
          ? ''
          : readinessHeadline(ready) || `${ready.pct}%`,
      ],
      styles: [S.label],
    },
    {
      values: ['Required still missing', result?.warnings?.preOfferMissingRequired ?? ''],
      styles: [S.label],
    },
    {
      values: ['Overdue', result?.warnings?.overdue ?? ''],
      styles: [S.label],
    },
    { values: ['Rows', 'Missing and Partial only. N/A, Upcoming and Complete are omitted. Warning only — nothing is gated.'], styles: [S.label, S.muted] },
  ];
  const rows = [headerRow(HEADERS), ...gapRows(result).map(rowOf)];
  return buildWorkbook([
    { name: 'Cover', rows: cover },
    { name: 'Gap list', rows },
  ]);
}

export function downloadGapListCsv(result, filename = 'diligence-gap-list.csv') {
  const blob = new Blob([gapListCsv(result)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadGapListXlsx(result, filename = 'diligence-gap-list.xlsx', opts) {
  downloadBytes(filename, buildGapListXlsx(result, opts), XLSX_MIME);
}
